import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    installActionKey,
    installHowToKey,
    resolveInstallOffer,
} from '../../domain/pwa/installOffer';
import { promptForInstall, subscribeInstallPromptState } from './installPrompt';
import {
    countVisit,
    readInstallCompletedAt,
    readInstallDismissedAt,
    writeInstallDismissed,
} from './offerPreferences';

/**
 * Whether this reader should be offered the install right now, and what the card
 * should say if so.
 *
 * One offer, because there is one thing to offer: Legends has no account, no
 * notifications and no backend, so the only question it ever asks is whether to
 * keep the app. The hook exists all the same rather than being inlined in the
 * card, because the DECISION needs three facts the component has no business
 * gathering — what this browser can do, how many times it has been here, and
 * what it did the last time it was asked.
 */

export function useAppOffers({ now = null } = {}) {
    const [promptState, setPromptState] = useState({ support: 'unsupported', isStandalone: false });
    // Counted once per page load, in an effect rather than in render — a render
    // can run twice under StrictMode, and a visit that counts twice reaches the
    // install threshold half a visit early.
    const [visitCount, setVisitCount] = useState(0);
    /**
     * The stored stamps, read once and then kept in step here.
     *
     * State rather than a read inside the memo below: a memo that reaches into
     * storage is not a function of its dependencies, so it would go stale the
     * moment anything else wrote a stamp, and it would read Web Storage on every
     * render of every screen that carries the offer.
     */
    const [stamps, setStamps] = useState(() => ({
        installDismissed: readInstallDismissedAt(),
        installed: readInstallCompletedAt(),
    }));
    // One clock for the whole mount. The hold is measured in weeks, so re-reading
    // it every render would buy nothing and make the memo recompute forever.
    const [mountedAt] = useState(() => Date.now());
    const evaluatedAt = now === null ? mountedAt : now;

    useEffect(() => {
        setVisitCount(countVisit());
    }, []);

    useEffect(() => subscribeInstallPromptState((nextPromptState) => {
        setPromptState(nextPromptState);
        // `appinstalled` reaches us through here, and it is what records the
        // install — including one done from the browser's own menu, which this
        // app never sees a prompt for.
        setStamps((current) => ({ ...current, installed: readInstallCompletedAt() }));
    }), []);

    const install = useMemo(() => resolveInstallOffer({
        support: promptState.support,
        visitCount,
        dismissedAt: stamps.installDismissed,
        installedAt: stamps.installed,
        now: evaluatedAt,
    }), [evaluatedAt, promptState.support, stamps.installDismissed, stamps.installed, visitCount]);

    const acceptInstall = useCallback(async () => {
        const { outcome } = await promptForInstall();

        // `unavailable` is every platform without `beforeinstallprompt`, where
        // the card's instruction IS the answer and must stay on screen.
        // `promptForInstall` has already recorded an accepted install; this
        // picks the stamp up so the card goes now rather than on the next mount.
        if (outcome === 'accepted') {
            setStamps((current) => ({ ...current, installed: readInstallCompletedAt() }));
        }

        return outcome;
    }, []);

    const dismissInstall = useCallback(() => {
        const at = Date.now();

        // Written before the state change, so a reload that beats the next paint
        // still agrees with what the reader just pressed.
        writeInstallDismissed(at);
        setStamps((current) => ({ ...current, installDismissed: at }));
    }, []);

    return {
        // Nothing at all until the visit count is known: it is filled by an
        // effect, so on the very first render it is 0 and the card would paint
        // for one frame before deciding it should not have.
        offer: visitCount > 0 && install.canOffer ? 'install' : null,
        installActionKey: installActionKey(promptState.support),
        installHowToKey: installHowToKey(promptState.support),
        acceptInstall,
        dismissInstall,
    };
}
